import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Typography
} from '@mui/material';
import { Form, Formik } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import CountryStateFields from 'src/common/formFields/CountryStateFields';
import InputField from 'src/common/formFields/InputField';
import SelectField from 'src/common/formFields/SelectField';
import AutocompleteField from 'src/common/formFields/AutocompleteField';
import Loader from 'src/common/Loader';
import { setErrorInfo, setSuccessInfo } from 'src/redux/modalSlice';
import { addApprover, updateApprover } from 'src/redux/approverSlice';
import FMSModal from 'src/common/FMSModal';
import { getAllSites } from 'src/redux/siteSlice';
import theme from 'src/theme';
import { DEFAULT_TIMEZONE, TIMEZONE_OPTIONS } from 'src/utils/timezoneUtils';
import {
  getApproverValidationSchema,
  approverFormModel,
  approverFormInitialValues
} from './approverFormModel';

const NewApprover = ({ open, handleClose, initialValues }) => {
  const dispatch = useDispatch();
  const { allSites } = useSelector((state) => state.sites);
  const [loading, setLoading] = useState(false);
  const requiredHintText = '';

  const formInitValues = initialValues
    ? { ...initialValues, timezone: initialValues.timezone || DEFAULT_TIMEZONE }
    : approverFormInitialValues;
  const isEditMode = Boolean(initialValues);

  const {
    formId,
    formField: {
      siteId,
      firstName,
      lastName,
      username,
      password,
      confirmPassword,
      email,
      mobileNum,
      empId,
      addressLine,
      city,
      state,
      country,
      timezone,
      pinCode
    }
  } = approverFormModel;

  useEffect(() => {
    if (open) {
      dispatch(getAllSites());
    }
  }, [open, dispatch]);

  const onSubmit = async (values, actions) => {
    setLoading(true);
    try {
      if (initialValues) {
        await dispatch(updateApprover(values)).unwrap();
      } else {
        await dispatch(addApprover(values)).unwrap();
      }
      handleClose();
      setLoading(false);
      dispatch(
        setSuccessInfo({
          successMsg: initialValues
            ? 'Approver details updated successfully!'
            : 'New Approver created successfully!'
        })
      );
    } catch (error) {
      actions.setSubmitting(false);
      setLoading(false);
      dispatch(setErrorInfo({ errorMsg: error?.error_message || 'Something went wrong' }));
    }
  };

  return (
    <FMSModal
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle
        sx={{
          fontSize: 18,
          fontWeight: 700,
          backgroundColor: '#FFFFFF',
          borderBottom: `1px solid ${theme.palette.divider}`
        }}
      >
        {initialValues ? 'UPDATE APPROVER' : 'ADD NEW APPROVER'}
      </DialogTitle>

      <Formik
        initialValues={formInitValues}
        validationSchema={getApproverValidationSchema(initialValues)}
        onSubmit={onSubmit}
      >
        {({ isSubmitting }) => (
          <>
            {loading ? (
              <Loader />
            ) : (
              <Form
                id={formId}
                className="csp-form csp-form--80vh"
              >
                <DialogContent
                  sx={{
                    flex: 1,
                    overflowY: 'auto',
                    px: 4,
                    py: 3
                  }}
                >
                  {!isEditMode && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                      * Required fields
                    </Typography>
                  )}
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                      <SelectField
                        name={siteId.name}
                        label={`${siteId.label} *`}
                        data={allSites}
                        required
                        requiredHintText={requiredHintText}
                        fullWidth
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <InputField
                        name={empId.name}
                        label={`${empId.label} *`}
                        required
                        requiredHintText={requiredHintText}
                        fullWidth
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <InputField
                        name={firstName.name}
                        label={`${firstName.label} *`}
                        required
                        requiredHintText={requiredHintText}
                        fullWidth
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <InputField
                        name={lastName.name}
                        label={`${lastName.label} *`}
                        required
                        requiredHintText={requiredHintText}
                        fullWidth
                      />
                    </Grid>

                    {!initialValues && (
                      <>
                        <Grid item xs={12} sm={6}>
                          <InputField
                            name={password.name}
                            label={`${password.label} *`}
                            type="password"
                            required
                            requiredHintText={requiredHintText}
                            fullWidth
                          />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                          <InputField
                            name={confirmPassword.name}
                            label={`${confirmPassword.label} *`}
                            type="password"
                            required
                            requiredHintText={requiredHintText}
                            fullWidth
                          />
                        </Grid>
                      </>
                    )}

                    <Grid item xs={12} sm={6}>
                      <InputField
                        name={username.name}
                        label={`${username.label} *`}
                        required
                        requiredHintText={requiredHintText}
                        fullWidth
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <InputField
                        name={email.name}
                        label={`${email.label} *`}
                        required
                        requiredHintText={requiredHintText}
                        fullWidth
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <InputField
                        name={mobileNum.name}
                        label={`${mobileNum.label} *`}
                        required
                        requiredHintText={requiredHintText}
                        fullWidth
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <InputField
                        name={addressLine.name}
                        label={`${addressLine.label} *`}
                        required
                        requiredHintText={requiredHintText}
                        fullWidth
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <InputField
                        name={city.name}
                        label={`${city.label} *`}
                        required
                        requiredHintText={requiredHintText}
                        fullWidth
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <InputField
                        name={pinCode.name}
                        label={`${pinCode.label} *`}
                        required
                        requiredHintText={requiredHintText}
                        fullWidth
                      />
                    </Grid>

                    <CountryStateFields
                      countryField={{ ...country, label: `${country.label} *` }}
                      stateField={{ ...state, label: `${state.label} *` }}
                      requiredHintText={requiredHintText}
                    />

                    <Grid item xs={12} sm={6}>
                      <AutocompleteField
                        name={timezone.name}
                        label={timezone.label}
                        options={TIMEZONE_OPTIONS}
                        placeholder="Search timezone"
                        required
                        fullWidth
                      />
                    </Grid>
                  </Grid>
                </DialogContent>

                <Divider />

                <DialogActions
                  sx={{
                    px: 3,
                    py: 2,
                    justifyContent: 'flex-end',
                    backgroundColor: '#FFFFFF'
                  }}
                >
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleClose}
                    sx={{
                      borderRadius: 2,
                      textTransform: 'none',
                      fontWeight: 600
                    }}
                  >
                    Cancel
                  </Button>

                  <Button
                    type="submit"
                    variant="contained"
                    disabled={isSubmitting}
                    sx={{
                      borderRadius: 2,
                      textTransform: 'none',
                      fontWeight: 600,
                      px: 3
                    }}
                  >
                    {initialValues ? 'Update Approver' : 'Create Approver'}
                  </Button>
                </DialogActions>
              </Form>
            )}
          </>
        )}
      </Formik>
    </FMSModal>
  );
};

NewApprover.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  initialValues: PropTypes.object
};

export default NewApprover;
